import React from 'react'

import {
  Box,
  Display,
  DisplayPosition,
  FontWeight,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { DietaryWarningTag } from '../Tags'

type RecipeTagColor = {
  light: string
  dark?: string
}

export type RecipeDietaryOverlayTag = {
  type: string
  text: string
}

export type RecipeTagLabel = {
  type: string
  text: string
  color?: RecipeTagColor
  backgroundColor?: RecipeTagColor
}

type InlinePosition = 'left' | 'right'
type RecipeTagVariant = 'default' | 'stacked'

const labelPillStylesByVariant: Record<
  RecipeTagVariant,
  { pill: React.CSSProperties; text?: { fontWeight?: FontWeight } }
> = {
  default: {
    pill: {
      borderRadius: '4px',
    },
  },
  stacked: {
    pill: {
      borderRadius: '0 4px 4px 0',
      opacity: 0.9,
    },
    text: {
      fontWeight: FontWeight.Bold,
    },
  },
}

const dietaryBadgeStylesByVariant: Record<
  RecipeTagVariant,
  { badge?: React.CSSProperties; text?: { fontWeight?: FontWeight } }
> = {
  default: {
    text: {
      fontWeight: FontWeight.Bold,
    },
  },
  stacked: {
    badge: {
      opacity: 0.9,
    },
    text: {
      fontWeight: FontWeight.Bold,
    },
  },
}

const DietaryBadge = withCitrusTheme(Box, (theme) => ({
  width: '28px',
  height: '28px',
  borderRadius: '999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.tokens.colors.ColdGrey_700,
  color: theme.tokens.colors.White,
}))

const LabelPill = withCitrusTheme(Box, () => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '24px',
  minWidth: 0,
  maxWidth: '100%',
  padding: '0 0.5rem',
}))

const getInlinePositionStyle = (position: InlinePosition, offset: string | number) =>
  position === 'right' ? { right: offset } : { left: offset }

export const RecipeDietaryTagOverlay = ({
  tags,
  variant = 'default',
  inlinePosition = 'right',
  inset = '1rem',
  dataTestId = 'recipe-tile-dietary-tags',
  style,
}: {
  tags: RecipeDietaryOverlayTag[]
  variant?: RecipeTagVariant
  inlinePosition?: InlinePosition
  inset?: string | number
  dataTestId?: string
  style?: React.CSSProperties
}) => {
  if (tags.length === 0) {
    return null
  }

  const variantStyles = dietaryBadgeStylesByVariant[variant]

  return (
    <Box
      data-testid={dataTestId}
      display={Display.Flex}
      gap={1}
      position={DisplayPosition.Absolute}
      style={{
        top: inset,
        zIndex: 2,
        ...getInlinePositionStyle(inlinePosition, inset),
        ...style,
      }}
    >
      {tags.map((tag) => (
        <DietaryBadge key={tag.type} style={variantStyles.badge}>
          <Text
            size={2}
            fontWeight={variantStyles.text?.fontWeight}
            style={{ color: 'inherit', lineHeight: '1' }}
          >
            {tag.text}
          </Text>
        </DietaryBadge>
      ))}
    </Box>
  )
}

export const RecipeTagLabels = ({
  tags,
  isOutOfStock = false,
  reserveEndSpace = false,
  stacked = false,
  inset = '1rem',
  dataTestId = 'recipe-tile-general-labels',
  style,
}: {
  tags: RecipeTagLabel[]
  isOutOfStock?: boolean
  reserveEndSpace?: boolean
  stacked?: boolean
  inset?: string | number
  dataTestId?: string
  style?: React.CSSProperties
}) => {
  if (!isOutOfStock && tags.length === 0) {
    return null
  }

  const wrapperStyle: React.CSSProperties = {
    position: DisplayPosition.Absolute,
    top: inset,
    left: inset,
    zIndex: 12,
    minWidth: 0,
    maxWidth: reserveEndSpace ? 'calc(100% - 88px)' : 'calc(100% - 2rem)',
    overflow: 'hidden',
    ...style,
  }

  const tagVariant: RecipeTagVariant = stacked ? 'stacked' : 'default'
  const variantStyles = labelPillStylesByVariant[tagVariant]

  if (isOutOfStock) {
    return (
      <Box data-testid="sold-out-tag" style={wrapperStyle}>
        <LabelPill
          style={{
            backgroundColor: '#4F5C68',
            ...variantStyles.pill,
            color: '#FFFFFF',
          }}
        >
          <Text
            size={2}
            fontWeight={variantStyles.text?.fontWeight}
            style={{ color: 'inherit', lineHeight: '24px' }}
          >
            Sold Out
          </Text>
        </LabelPill>
      </Box>
    )
  }

  return (
    <Box
      data-testid={dataTestId}
      display={Display.Flex}
      gap={stacked ? 2 : 1}
      style={{
        flexDirection: stacked ? 'column' : undefined,
        alignItems: stacked ? 'flex-start' : undefined,
        ...wrapperStyle,
      }}
    >
      {tags.map((label) => (
        <RecipeTagLabelPill
          key={`${label.type}-${label.text}`}
          label={label}
          variant={tagVariant}
        />
      ))}
    </Box>
  )
}

const RecipeTagLabelPill = ({
  label,
  variant = 'default',
}: {
  label: RecipeTagLabel
  variant?: RecipeTagVariant
}) => {
  if (label.type === 'invalidPreference') {
    return <DietaryWarningTag text={label.text} variant={variant} />
  }

  if (!label.color || !label.backgroundColor) {
    return null
  }

  const variantStyles = labelPillStylesByVariant[variant]

  return (
    <LabelPill
      style={{
        backgroundColor: label.backgroundColor.light,
        ...variantStyles.pill,
        color: label.color.light,
      }}
    >
      <Text
        size={2}
        fontWeight={variantStyles.text?.fontWeight}
        style={{
          color: 'inherit',
          lineHeight: '24px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        {label.text}
      </Text>
    </LabelPill>
  )
}
